import { Component, input, output, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { QuestionView, QuestionType } from '@formrails/form-builder/data-access';
import { FormContextMenuComponent } from '../form-context-menu/form-context-menu.component';
import { QuestionContainerComponent } from '../questions';
import { AddQuestionButtonComponent } from '../add-question-button/add-question-button.component';

/**
 * Component for displaying and managing the list of questions with drag-and-drop support.
 * Uses Angular signals for inputs and outputs.
 */
@Component({
  host: { '[class.preview-mode]': 'previewMode()' },
  selector: 'app-question-list',
  standalone: true,
  imports: [
    FormsModule,
    FormContextMenuComponent,
    QuestionContainerComponent,
    AddQuestionButtonComponent
  ],
  templateUrl: './question-list.component.html',
  styleUrl: './question-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuestionListComponent {
  readonly questions = input<QuestionView[]>([]);
  readonly showQuestionNumbers = input(true);
  readonly isReadOnly = input(false);
  /**
   * Display-only rendering (e.g. the saved-forms preview): hides the add
   * question buttons and takes its height from the container rather than
   * the builder page's viewport-based limit. Combine with `isReadOnly`.
   */
  readonly previewMode = input(false);
  readonly hoveredQuestionId = input<string | null>(null);
  readonly dragOverIndex = input<number | null>(null);
  readonly draggedQuestionId = input<string | null>(null);

  readonly questionChange = output<QuestionView>();
  readonly questionConfigChange = output<{ questionId: string; config: Partial<QuestionView> }>();
  readonly deleteQuestion = output<string>();
  readonly insertBelow = output<string>();
  readonly addNew = output<void>();
  readonly sortQuestions = output<void>();
  readonly toggleRequired = output<string>();
  readonly acceptQuestion = output<string>();
  readonly editQuestion = output<string>();
  readonly questionRowMouseEnter = output<{ questionId: string; isLast: boolean }>();
  readonly questionRowMouseLeave = output<{ questionId: string; event: MouseEvent }>();
  readonly dragStart = output<{ event: DragEvent; questionId: string }>();
  readonly dragEnd = output<void>();
  readonly dragOver = output<{ event: DragEvent; index: number }>();
  readonly dragLeave = output<void>();
  readonly drop = output<{ event: DragEvent; index: number }>();

  readonly QuestionType = QuestionType;

  isQuestionMenuVisible(question: QuestionView, _isLast: boolean): boolean {
    return this.hoveredQuestionId() === question.id || (question.isEditing ?? true);
  }

  onQuestionRowMouseEnter(questionId: string, isLast: boolean): void {
    this.questionRowMouseEnter.emit({ questionId, isLast });
  }

  onQuestionRowMouseLeave(questionId: string, event: MouseEvent): void {
    this.questionRowMouseLeave.emit({ questionId, event });
  }

  onDragStart(event: DragEvent, questionId: string): void {
    this.dragStart.emit({ event, questionId });
  }

  onDragEnd(): void {
    this.dragEnd.emit();
  }

  onDragOver(event: DragEvent, index: number): void {
    this.dragOver.emit({ event, index });
  }

  onDragLeave(): void {
    this.dragLeave.emit();
  }

  onDrop(event: DragEvent, index: number): void {
    this.drop.emit({ event, index });
  }

  onQuestionChange(question: QuestionView): void {
    this.questionChange.emit(question);
  }

  onQuestionConfigChange(questionId: string, config: Partial<QuestionView>): void {
    this.questionConfigChange.emit({ questionId, config });
  }

  onDeleteQuestion(questionId: string): void {
    this.deleteQuestion.emit(questionId);
  }

  onInsertBelow(questionId: string): void {
    this.insertBelow.emit(questionId);
  }

  onAddNew(): void {
    this.addNew.emit();
  }

  onSortQuestions(): void {
    this.sortQuestions.emit();
  }

  onToggleRequired(questionId: string): void {
    this.toggleRequired.emit(questionId);
  }

  onAcceptQuestion(questionId: string): void {
    this.acceptQuestion.emit(questionId);
  }

  onEditQuestion(questionId: string): void {
    this.editQuestion.emit(questionId);
  }
}

